import { Location } from "@angular/common";
import { inject, Injectable, NgZone, OnDestroy, DOCUMENT } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
    Event,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationSkipped,
    Router,
    RouterEvent,
} from "@angular/router";
import { LoadingBarService } from "@ngx-loading-bar/core";
import { firstValueFrom, Subscription, take } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";

import { LOCALE, WINDOW } from "@hermes/core-app-core";
import {
    ENABLE_INP_PATCH_SPA,
    FeatureFlagFacade,
    FeatureFlagName,
} from "@hermes/hermes-web-states-flipper-state";
import {
    initFocusOnElement,
    nextPaint,
    stripLocaleFromUrl,
} from "@hermes/utils-generic-helpers";
import { BreakpointService } from "@hermes/utils-generic-services-user-interface";

type EndOfNavigation =
    | NavigationEnd
    | NavigationError
    | NavigationCancel
    | NavigationSkipped;
export const isEndOfNavigation = (
    routerEvent: Event,
): routerEvent is EndOfNavigation =>
    routerEvent instanceof NavigationEnd ||
    routerEvent instanceof NavigationError ||
    routerEvent instanceof NavigationSkipped ||
    routerEvent instanceof NavigationCancel;
export const urlWithoutFragment = (routerEvent: RouterEvent): string =>
    routerEvent.url.split("#")[0];

@Injectable()
export class RouterService implements OnDestroy {
    private readonly window = inject<Window>(WINDOW);
    private readonly document = inject(DOCUMENT);
    private readonly locale = inject(LOCALE);
    private readonly router = inject(Router);
    private readonly featureFlagFacade = inject(FeatureFlagFacade);
    private readonly loadingBarService = inject(LoadingBarService);
    private readonly breakpointService = inject(BreakpointService);
    private readonly location = inject(Location);
    private readonly ngZone = inject(NgZone);

    private readonly isINPPatchSPAEnabled = toSignal(
        this.featureFlagFacade.isActivated(ENABLE_INP_PATCH_SPA),
        { initialValue: false },
    );

    public pageChanged$ = this.router.events.pipe(
        map((event) => event as RouterEvent),
        filter((event) => event instanceof NavigationEnd),
        map(urlWithoutFragment),
        distinctUntilChanged(),
    );

    private subscriptions: Subscription = new Subscription();

    /**
     * Navigate to path via Angular's router for full pages when the feature exist and is activated
     * Else, do a native redirect
     *
     * @param pathWithLocale path to redirect to
     * @param featureFlag SPA routing flag
     */
    public async navigate(
        pathWithLocale: string,
        featureFlag?: FeatureFlagName,
    ): Promise<void> {
        if (featureFlag) {
            const flag = await firstValueFrom(
                this.featureFlagFacade.isActivated(featureFlag),
            );

            if (flag) {
                return this.spaRedirect(pathWithLocale);
            }
            return this.nativeRedirect(pathWithLocale);
        }

        return this.nativeRedirect(pathWithLocale);
    }

    public resetFocus(): void {
        const beforeFirstA11yLink: HTMLElement = this.document.getElementById(
            "skip-links-accessibility",
        ) as HTMLElement;

        const beforeLogo: HTMLElement = this.document.getElementById(
            "logo-accessibility",
        ) as HTMLElement;

        const viewportLarge = this.breakpointService.mediumBreakpointMatcher();

        const focusTarget = viewportLarge ? beforeFirstA11yLink : beforeLogo;

        if (focusTarget) {
            initFocusOnElement(focusTarget);
        }

        if (!viewportLarge) {
            this.ngZone.runOutsideAngular(() => {
                setTimeout(() => this.window.scrollTo(0, 0)); // Force scroll since focus don't trigger an implicit scroll on mobile
            });
        }
    }

    /**
     * Redirect user via Angular's Router.
     *
     * @param pathWithLocale path to redirect to
     */
    public async spaRedirect(pathWithLocale: string): Promise<void> {
        // Trigger the loading bar
        this.loadingBarService.useRef().start();

        // Wait until the browser is ready to paint the next frame
        // It gives the browser a chance to render the loading bar before starting the SPA navigation.
        if (this.isINPPatchSPAEnabled()) {
            await nextPaint();
        }

        this.subscriptions.add(
            this.pageChanged$.pipe(take(1)).subscribe(() => {
                this.resetFocus();
            }),
        );

        // Remove urlPrefix as we are already in the localized application (avoid navigating to "/us/en/us/en/...")
        await this.router.navigateByUrl(
            this.stripLocaleFromUrl(pathWithLocale),
            { state: { previousUrl: this.location.path() } },
        );

        this.loadingBarService.useRef().complete();
    }

    public stripLocaleFromUrl(url: string): string {
        return stripLocaleFromUrl(url, this.locale);
    }

    /**
     * Redirect user via browser.
     *
     * @param pathWithLocale path to redirect to
     * @param isNewTab true to open in new tab
     */
    public nativeRedirect(pathWithLocale: string, isNewTab = false): void {
        if (isNewTab) {
            this.window
                .open(pathWithLocale, "_blank", "noopener,noreferrer")
                ?.focus();
            return;
        }

        const historyState = this.window.history.state;
        if (historyState) {
            historyState.previousUrl = this.location.path();
        }

        this.window.location.href = pathWithLocale;
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
